import type React from "react";
import {useMemo} from "react";
import {useAuthState} from "react-firebase-hooks/auth";
import {useNavigate} from "react-router-dom";

import {faGamepad, faStar, faUser} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {usePlaylists, useRealtimePlaylists} from "~/api/hooks/usePlaylist";
import AppHeader from "~/component/common/AppHeader";
import CustomButton from "~/component/common/CustomButton";
import UserName from "~/component/common/UserName";
import {auth} from "~/firebase/firebase";

import CreatePlaylistModalButton from "./create-new/CreatePlaylistModalButton";

const PlaylistSelector: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);

  const {data: featuredPlaylists} = usePlaylists({
    key: {},
    filters: [
      {
        fieldPath: "featured",
        opStr: "==",
        value: true,
      },
    ],
    limit: 100,
  });
  useRealtimePlaylists({
    key: {},
    filters: [
      {
        fieldPath: "featured",
        opStr: "==",
        value: true,
      },
    ],
    limit: 100,
  });

  const {data: myPlaylists} = usePlaylists({
    key: {},
    filters: [
      {
        fieldPath: "creatorId",
        opStr: "==",
        value: user?.uid ?? "unknown",
      },
    ],
    sort: [{fieldPath: "updatedAt", direction: "desc"}],
    limit: 100,
  });
  useRealtimePlaylists({
    key: {},
    filters: [
      {
        fieldPath: "creatorId",
        opStr: "==",
        value: user?.uid ?? "unknown",
      },
    ],
    sort: [{fieldPath: "updatedAt", direction: "desc"}],
    limit: 100,
  });

  const mergedPlaylists = useMemo(() => {
    // Sort featured playlists by featuredRank in memory
    const featured = [...(featuredPlaylists || [])].sort((a, b) => {
      // Handle undefined featuredRank values
      const rankA = a.featuredRank ?? Number.MAX_SAFE_INTEGER;
      const rankB = b.featuredRank ?? Number.MAX_SAFE_INTEGER;
      return rankA - rankB; // Sort in ascending order
    });

    const myOwn = myPlaylists || [];
    const idSet = new Set();
    const uniqueList = [];

    for (const playlist of featured) {
      if (!idSet.has(playlist.id)) {
        uniqueList.push(playlist);
        idSet.add(playlist.id);
      }
    }

    for (const playlist of myOwn) {
      if (!idSet.has(playlist.id)) {
        uniqueList.push(playlist);
        idSet.add(playlist.id);
      }
    }

    return uniqueList;
  }, [featuredPlaylists, myPlaylists]);

  return (
    <div className="flex flex-col w-screen min-h-screen overflow-auto relative text-white bg-transparent">
      <AppHeader title="Play" />

      <div className="fixed bottom-2 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 z-50 pointer-events-auto">
        <CreatePlaylistModalButton />
      </div>

      <main className="mt-16 px-2 pb-4 flex-1 z-20">
        <div className="flex flex-wrap gap-2 justify-center">
          {mergedPlaylists.map((playlist) => (
            <CustomButton
              key={playlist.id}
              onClick={() => navigate(`/playlist/${playlist.id}`)}
              className="w-[300px] cursor-pointer bg-gradient-to-b from-black/50 to-indigo-900/50 rounded-lg p-2 shadow-sm transition duration-300 hover:from-black/90 hover:to-indigo-900/90 text-left"
            >
              <h2 className="text-blue-100 text-lg font-normal mb-1 flex items-center">
                {playlist.featured && (
                  <FontAwesomeIcon
                    icon={faStar}
                    className="text-amber-400 mr-1.5 animate-pulse"
                    size="sm"
                  />
                )}
                <span className="truncate">{playlist.name}</span>
              </h2>
              <p className="text-blue-200 text-sm mb-1 truncate">
                {playlist.description ?? ":D"}
              </p>
              <div className="text-blue-200 text-xs flex flex-col">
                <span className="flex items-center truncate">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="mr-1 text-blue-300"
                  />
                  <span className="mr-1">by</span>
                  <UserName userId={playlist.creatorId} />
                </span>
                <span className="flex items-center mt-1">
                  <FontAwesomeIcon
                    icon={faGamepad}
                    className="mr-1 text-blue-300"
                  />
                  <span>{playlist.levelIds?.length || 0} levels</span>
                </span>
              </div>
            </CustomButton>
          ))}
        </div>
      </main>
    </div>
  );
};

export default PlaylistSelector;
